import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { ButtonSpinner, Loader } from '../../ui/Loader';
import {
  useGetServerOptionsQuery,
  useGetServerNextOptionsQuery,
  useGetOrderSummaryMutation,
  usePurchaseProxyMutation,
} from '../../../store/api/proxiesApi';
import { ProxyOrderSummaryData, Server2Options, Server2NextOptions } from '../../../types/api';

const SERVER_ID = 2;

export function Server2Wizard() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<'country' | 'config' | 'review'>('country');
  const [countrySearch, setCountrySearch] = useState('');
  const [selectedCountry, setSelectedCountry] = useState<string>('');
  const [citySearch, setCitySearch] = useState('');
  const [cityDropdownOpen, setCityDropdownOpen] = useState(false);
  const cityDropdownRef = useRef<HTMLDivElement>(null);
  const [selectedCity, setSelectedCity] = useState<string>('');
  const [selectedType, setSelectedType] = useState<'dedicated' | 'shared'>('dedicated');
  const [selectedIpVersion, setSelectedIpVersion] = useState<4 | 6>(4);
  const selectedPeriod = 30;
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<ProxyOrderSummaryData | null>(null);
  const quantity = 1;

  const { data: optionsData, isLoading: loadingOptions } = useGetServerOptionsQuery(SERVER_ID);
  const [purchaseProxy, { isLoading: isPurchasing }] = usePurchaseProxyMutation();
  const [getOrderSummary, { isLoading: loadingSummary }] = useGetOrderSummaryMutation();

  const { data: nextOptionsData, isLoading: loadingNextOptions } =
    useGetServerNextOptionsQuery(
      {
        serverId: SERVER_ID,
        params: { country: selectedCountry },
      },
      { skip: !selectedCountry || step === 'country' }
    );

  const options = optionsData as Server2Options | undefined;
  const nextOptionsRaw = nextOptionsData as Server2NextOptions | undefined;
  const optionsPayload = (options as { data?: Server2Options })?.data ?? options;
  const nextOptionsPayload = (nextOptionsRaw as { data?: Server2NextOptions })?.data ?? nextOptionsRaw;
  const nextOptions = nextOptionsPayload?.nextOptions;

  const allCountries = optionsPayload?.options?.countries ?? [];
  const countries = countrySearch.trim()
    ? allCountries.filter(
        (c) =>
          c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
          c.code.toLowerCase().includes(countrySearch.toLowerCase())
      )
    : allCountries;

  const allCities = nextOptions?.cities ?? [];
  const cities = citySearch.trim()
    ? allCities.filter(
        (c) =>
          c.name.toLowerCase().includes(citySearch.toLowerCase()) ||
          c.code.toLowerCase().includes(citySearch.toLowerCase())
      )
    : allCities;

  // Close city dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (cityDropdownRef.current && !cityDropdownRef.current.contains(e.target as Node)) {
        setCityDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountry(countryCode);
    setStep('config');
    setError(null);
  };

  const handleBack = () => {
    if (step === 'config') {
      setStep('country');
      setSelectedCountry('');
      setSelectedCity('');
      setCountrySearch('');
      setCitySearch('');
    } else if (step === 'review') {
      setStep('config');
      setSummary(null);
    }
  };

  const handleNext = () => {
    setError(null);
    if (step === 'config') {
      if (!selectedType || !selectedIpVersion) {
        setError(t('proxies.wizard.errors.requiredFields'));
        return;
      }
      setSummary(null);
      setStep('review');
    }
  };

  // Fetch order summary when on review step (Server 2)
  useEffect(() => {
    if (step !== 'review' || !selectedCountry) return;
    setError(null);
    getOrderSummary({
      serverId: 2,
      body: {
        type: selectedType,
        ipVersion: selectedIpVersion,
        country: selectedCountry,
        period: selectedPeriod,
        quantity,
      },
    })
      .unwrap()
      .then((res) => setSummary(res.data))
      .catch((err: any) => {
        const msg = err?.data?.message || err?.message || t('proxies.purchase.error');
        setError(msg);
        setSummary(null);
      });
  }, [step, selectedCountry, selectedType, selectedIpVersion]);

  const handlePurchase = async () => {
    setError(null);
    try {
      const response = await purchaseProxy({
        serverId: 2,
        type: selectedType,
        ipVersion: selectedIpVersion,
        country: selectedCountry,
        city: selectedCity || undefined,
        period: selectedPeriod,
        quantity,
      }).unwrap();

      toast.success(t('proxies.purchase.success'));
      if (response.data.proxies && response.data.proxies.length > 0 && response.data.proxies[0]) {
        navigate(`/proxies/${response.data.proxies[0]._id}`);
      } else {
        navigate('/proxies/list');
      }
    } catch (err: any) {
      const errorMessage = err?.data?.message || err?.message || t('proxies.purchase.error');
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  if (loadingOptions) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-between gap-1 sm:gap-4">
          <div
            className={`flex items-center min-w-0 flex-shrink-0 ${step === 'country' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                step === 'country'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              1
            </div>
            <span className="ml-1 sm:ml-2 font-body truncate hidden sm:inline">
              {t('proxies.wizard.server2.selectCountry')}
            </span>
          </div>
          <div className="flex-1 min-w-2 h-0.5 bg-neutral-dark flex-shrink" />
          <div
            className={`flex items-center min-w-0 flex-shrink-0 ${step === 'config' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                step === 'config'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              2
            </div>
            <span className="ml-1 sm:ml-2 font-body truncate hidden sm:inline">
              {t('proxies.wizard.server2.configure')}
            </span>
          </div>
          <div className="flex-1 min-w-2 h-0.5 bg-neutral-dark flex-shrink" />
          <div
            className={`flex items-center min-w-0 flex-shrink-0 ${step === 'review' ? 'text-brand-accent' : 'text-neutral-medium'}`}
          >
            <div
              className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 flex-shrink-0 ${
                step === 'review'
                  ? 'border-brand-accent bg-brand-accent/20'
                  : 'border-neutral-medium'
              }`}
            >
              3
            </div>
            <span className="ml-1 sm:ml-2 font-body truncate hidden sm:inline">
              {t('proxies.wizard.review')}
            </span>
          </div>
        </div>
        {/* Current step label on mobile (below bar) */}
        <p className="sm:hidden mt-2 text-center font-body text-brand-accent text-sm">
          {step === 'country' && t('proxies.wizard.server2.selectCountry')}
          {step === 'config' && t('proxies.wizard.server2.configure')}
          {step === 'review' && t('proxies.wizard.review')}
        </p>
      </div>

      {/* Step 1: Select Country - list with search inside */}
      {step === 'country' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server2.selectCountry')}
          </h2>
          <div className="border border-neutral-dark rounded-lg bg-neutral-darker overflow-hidden">
            <div className="p-2 border-b border-neutral-dark bg-neutral-darker sticky top-0">
              <input
                type="text"
                value={countrySearch}
                onChange={(e) => setCountrySearch(e.target.value)}
                placeholder={t('proxies.wizard.server1.searchCountry')}
                className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body placeholder-neutral-medium"
              />
            </div>
            <ul className="max-h-80 overflow-y-auto py-1">
              {countries.map((country) => (
                <li key={country.code}>
                  <button
                    type="button"
                    onClick={() => handleCountrySelect(country.code)}
                    className="w-full px-4 py-3 text-left font-body text-neutral-light hover:bg-brand-accent/20 border-b border-neutral-dark/50 last:border-0 transition-colors flex justify-between items-center"
                  >
                    <span className="font-medium">{country.name}</span>
                    <span className="text-neutral-medium text-sm">{country.code.toUpperCase()}</span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Step 2: Configure */}
      {step === 'config' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.server2.configure')}
          </h2>
          {loadingNextOptions ? (
            <div className="flex justify-center py-12">
              <Loader />
            </div>
          ) : (
            <div className="space-y-6">
              {/* City - dropdown with search inside list */}
              {allCities.length > 0 && (
                <div ref={cityDropdownRef} className="relative">
                  <label className="block text-neutral-light font-body mb-2">
                    {t('proxies.wizard.server2.city')} ({t('proxies.wizard.optional')})
                  </label>
                  <button
                    type="button"
                    onClick={() => setCityDropdownOpen((o) => !o)}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-left text-neutral-light font-body flex justify-between items-center"
                  >
                    <span className={selectedCity ? '' : 'text-neutral-medium'}>
                      {selectedCity
                        ? allCities.find((c) => c.code === selectedCity)?.name ?? selectedCity
                        : t('proxies.wizard.selectOption')}
                    </span>
                    <span className="text-neutral-medium">▾</span>
                  </button>
                  {cityDropdownOpen && (
                    <div className="absolute z-10 mt-1 w-full rounded-lg border border-neutral-dark bg-neutral-dark shadow-xl max-h-72 flex flex-col overflow-hidden">
                      <div className="p-2 border-b border-neutral-dark sticky top-0 bg-neutral-dark">
                        <input
                          type="text"
                          value={citySearch}
                          onChange={(e) => setCitySearch(e.target.value)}
                          placeholder={t('common.search')}
                          className="w-full px-3 py-2 bg-neutral-dark border border-neutral-dark rounded text-neutral-light font-body text-sm placeholder-neutral-medium"
                          autoFocus
                        />
                      </div>
                      <ul className="overflow-y-auto flex-1 py-1 max-h-56 bg-neutral-dark">
                        {cities.map((city) => (
                          <li key={city.code}>
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedCity(city.code);
                                setCityDropdownOpen(false);
                                setCitySearch('');
                              }}
                              className={`w-full px-4 py-2 text-left font-body text-sm hover:bg-brand-accent/20 ${
                                selectedCity === city.code ? 'bg-brand-accent/30 text-brand-accent' : 'text-neutral-light'
                              }`}
                            >
                              {city.name}
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {/* Type */}
              {nextOptions?.types && (
                <div>
                  <label className="block text-neutral-light font-body mb-2">
                    {t('proxies.wizard.server2.type')}
                  </label>
                  <select
                    value={selectedType}
                    onChange={(e) => setSelectedType(e.target.value as 'dedicated' | 'shared')}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                  >
                    {nextOptions.types.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* IP Version */}
              {nextOptions?.ipVersions && (
                <div>
                  <label className="block text-neutral-light font-body mb-2">
                    {t('proxies.wizard.server2.ipVersion')}
                  </label>
                  <select
                    value={selectedIpVersion}
                    onChange={(e) => setSelectedIpVersion(Number(e.target.value) as 4 | 6)}
                    className="w-full px-4 py-2 bg-neutral-dark border border-neutral-dark rounded-lg text-neutral-light font-body"
                  >
                    {nextOptions.ipVersions.map((version) => (
                      <option key={version} value={version}>
                        IPv{version}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Period - fixed 30 days (no selector) */}
              <div className="text-neutral-medium font-body text-sm">
                {t('proxies.wizard.server2.period')}: 30 {t('proxies.wizard.days')}
              </div>

              {error && (
                <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body">
                  {error}
                </div>
              )}

              <div className="flex gap-4">
                <button
                  onClick={handleBack}
                  className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body"
                >
                  {t('common.back')}
                </button>
                <button
                  onClick={handleNext}
                  className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 3: Review */}
      {step === 'review' && (
        <div className="card-glass rounded-2xl shadow-2xl p-8">
          <h2 className="text-2xl font-heading font-semibold text-neutral-light mb-6">
            {t('proxies.wizard.review')}
          </h2>
          <div className="space-y-4 mb-6">
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server2.country')}:</span>
              <span className="text-neutral-light font-body">
                {allCountries.find((c) => c.code === selectedCountry)?.name ?? selectedCountry}
              </span>
            </div>
            {selectedCity && (
              <div className="flex justify-between">
                <span className="text-neutral-medium font-body">{t('proxies.wizard.server2.city')}:</span>
                <span className="text-neutral-light font-body">
                  {nextOptions?.cities?.find((c) => c.code === selectedCity)?.name}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server2.type')}:</span>
              <span className="text-neutral-light font-body">{selectedType}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server2.ipVersion')}:</span>
              <span className="text-neutral-light font-body">IPv{selectedIpVersion}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-neutral-medium font-body">{t('proxies.wizard.server2.period')}:</span>
              <span className="text-neutral-light font-body">
                30 {t('proxies.wizard.days')}
              </span>
            </div>
            {loadingSummary && (
              <div className="flex items-center justify-center py-2">
                <ButtonSpinner size="sm" className="text-brand-accent" />
              </div>
            )}
            {summary && !loadingSummary && (
              <div className="pt-4 mt-4 border-t border-neutral-dark space-y-1">
                <div className="flex justify-between items-center">
                  <span className="text-neutral-medium font-body">
                    {t('proxies.wizard.server1.finalPrice')}
                    {summary.isEstimate && (
                      <span className="ml-2 text-sm text-amber-400">
                        ({t('proxies.wizard.server1.priceEstimate')})
                      </span>
                    )}
                  </span>
                  <span className="text-neutral-light font-body font-semibold">
                    {(() => {
                      const finalPrice = Number(summary.finalPrice);
                      return Number.isFinite(finalPrice)
                        ? finalPrice.toFixed(2)
                        : String(summary.finalPrice);
                    })()} {summary.currency}
                  </span>
                </div>
              </div>
            )}
          </div>

          {error && (
            <div className="p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 font-body mb-6">
              {error}
            </div>
          )}

          <div className="flex gap-4">
            <button
              onClick={handleBack}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-neutral-dark text-neutral-light rounded-lg hover:bg-neutral-dark/80 transition-all font-body disabled:opacity-50"
            >
              {t('common.back')}
            </button>
            <button
              onClick={handlePurchase}
              disabled={isPurchasing || loadingSummary}
              className="flex-1 px-6 py-3 bg-brand-accent text-brand-primary rounded-lg hover:shadow-lg hover:shadow-brand-accent/50 transition-all font-body font-semibold disabled:opacity-50"
            >
              {isPurchasing ? t('common.processing') : t('proxies.purchase.cta')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

